// Analytics utility - GA4 + PostHog dual tracking
import posthog from "posthog-js";
import { BRAND } from "@/lib/posthog";

const GA_MEASUREMENT_ID = "G-F3CHG7V4GJ";
const UTM_STORAGE_KEY = "immovox_utm_params";

// Contexte commun ajouté à chaque capture PostHog
const getPosthogContext = () => ({
  brand: BRAND,
  path: typeof window !== "undefined" ? window.location.pathname : undefined,
});


type EventParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Get stored UTM params for automatic propagation
const getStoredUtm = (): Record<string, string> => {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

export const trackEvent = (
  eventName: string,
  params?: EventParams
): void => {
  // Google Consent Mode v2 handles consent natively
  // Events are sent, Google decides what to do based on consent state
  
  const utmParams = getStoredUtm();
  const enrichedParams = { ...utmParams, ...params };

  if (import.meta.env.DEV) {
    console.log(`[Analytics] Event: ${eventName}`, enrichedParams);
  }

  if (window.gtag) {
    window.gtag("event", eventName, enrichedParams);
  }

  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...enrichedParams,
    });
  }

  // PostHog
  posthog.capture(eventName, { ...enrichedParams, ...getPosthogContext() });

};

// Track conversion events (these are sent to GA4 as conversions)
export const trackConversion = (
  conversionName: string,
  value?: number,
  params?: EventParams
): void => {
  // Google Consent Mode v2 handles consent natively
  
  const utmParams = getStoredUtm();
  const enrichedParams = {
    send_to: GA_MEASUREMENT_ID,
    value: value,
    currency: "EUR",
    ...utmParams,
    ...params,
  };

  if (import.meta.env.DEV) {
    console.log(`[Analytics] Conversion: ${conversionName}`, enrichedParams);
  }

  if (window.gtag) {
    window.gtag("event", conversionName, enrichedParams);
  }

  if (window.dataLayer) {
    window.dataLayer.push({
      event: conversionName,
      conversion_value: value,
      ...utmParams,
      ...params,
    });
  }

  // PostHog
  posthog.capture(conversionName, {
    ...enrichedParams,
    ...getPosthogContext(),
    is_conversion: true,
  });

};

// Set user ID to link pre/post login sessions
export const setUserId = (userId: string): void => {
  // Google Consent Mode v2 handles consent natively

  if (import.meta.env.DEV) {
    console.log(`[Analytics] setUserId: ${userId}`);
  }

  if (window.gtag) {
    window.gtag("config", GA_MEASUREMENT_ID, { user_id: userId });
  }

  if (window.dataLayer) {
    window.dataLayer.push({
      user_id: userId,
    });
  }

  // PostHog — links pre/post login sessions
  posthog.identify(userId);
};

// Pre-defined events
export const Analytics = {
  // ══════════════════════════════════════════════════════════
  // CONVERSION EVENTS (mark as conversions in GA4)
  // ══════════════════════════════════════════════════════════
  
  // Demo booking completed (Calendly event scheduled)
  demoBooked: (source: string, utmParams?: Record<string, string>) => {
    trackConversion("demo_booked", 0, {
      source,
      event_category: "conversion",
      event_label: "calendly_booking",
      ...utmParams,
    });
  },

  // User signed up / account created
  signupCompleted: (method: string = "email") => {
    trackConversion("sign_up", 0, {
      method,
      event_category: "conversion",
    });
  },

  // Trial started
  trialStarted: (plan?: string) => {
    trackConversion("trial_started", 0, {
      plan: plan || "standard",
      event_category: "conversion",
    });
  },

  // Payment completed
  paymentCompleted: (amount: number, plan: string) => {
    trackConversion("purchase", amount, {
      plan,
      event_category: "conversion",
      transaction_id: `txn_${Date.now()}`,
    });
  },

  // Auth page viewed - track visitors landing on auth page (before any form interaction)
  authPageViewed: (mode: "login" | "signup") => {
    // Google Consent Mode v2 handles consent natively

    if (import.meta.env.DEV) {
      console.log(`[Analytics] auth_page_viewed - mode: ${mode}`);
    }

    if (window.gtag) {
      window.gtag("event", "auth_page_viewed", {
        auth_mode: mode,
        ...getStoredUtm(),
      });
    }
  },

  // Google Ads conversion - triggered after checkout completion (trial or paid)
  adsConversionPurchase: () => {
    // Google Consent Mode v2 handles consent natively

    if (import.meta.env.DEV) {
      console.log(`[Analytics] Google Ads Conversion: ads_conversion_PURCHASE_1`);
    }

    if (window.gtag) {
      window.gtag("event", "ads_conversion_PURCHASE_1", {});
    }
  },

  // ══════════════════════════════════════════════════════════
  // FUNNEL EVENTS (signup -> onboarding -> checkout)
  // ══════════════════════════════════════════════════════════

  // Signup form submitted (before validation)
  signupFormSubmitted: (method: string = "email") => {
    trackEvent("signup_form_submitted", {
      method,
      event_category: "funnel",
    });
  },

  // Onboarding page loaded
  onboardingStarted: (source?: string) => {
    trackEvent("onboarding_started", {
      source: source || "direct",
      event_category: "funnel",
    });
  },

  // Onboarding form submitted
  onboardingSubmitted: (agencyName: string) => {
    trackEvent("onboarding_submitted", {
      agency_name: agencyName,
      event_category: "funnel",
    });
  },

  // Checkout page viewed
  checkoutViewed: () => {
    trackEvent("checkout_viewed", {
      event_category: "funnel",
    });
  },

  // Checkout button clicked (redirect to Stripe)
  checkoutInitiated: () => {
    trackEvent("checkout_initiated", {
      event_category: "funnel",
    });
  },

  // ══════════════════════════════════════════════════════════
  // ERROR EVENTS
  // ══════════════════════════════════════════════════════════

  // Form validation error
  formError: (formName: string, fieldName: string, errorMessage: string) => {
    trackEvent("form_error", {
      form_name: formName,
      field_name: fieldName,
      error_message: errorMessage,
      event_category: "error",
    });
  },

  // API call error
  apiError: (endpoint: string, statusCode: number, errorMessage?: string) => {
    trackEvent("api_error", {
      endpoint,
      status_code: statusCode,
      error_message: errorMessage,
      event_category: "error",
    });
  },

  // ══════════════════════════════════════════════════════════
  // SECTION VISIBILITY EVENTS
  // ══════════════════════════════════════════════════════════

  // Landing page section became visible
  sectionViewed: (sectionName: string) => {
    trackEvent("section_viewed", {
      section_name: sectionName,
      event_category: "engagement",
    });
  },

  // ══════════════════════════════════════════════════════════
  // BLOG EVENTS
  // ══════════════════════════════════════════════════════════

  // Blog article page viewed
  blogArticleViewed: (slug: string, title: string, readingTime?: number) => {
    trackEvent("blog_article_viewed", {
      article_slug: slug,
      article_title: title,
      reading_time: readingTime,
      event_category: "blog",
    });
  },

  // Blog article scroll depth reached
  blogScrollDepth: (slug: string, percentage: number) => {
    trackEvent("blog_scroll_depth", {
      article_slug: slug,
      percentage,
      event_category: "blog",
    });
  },

  // CTA clicked within blog article
  blogCtaClicked: (slug: string, ctaName: string) => {
    trackEvent("blog_cta_clicked", {
      article_slug: slug,
      cta_name: ctaName,
      event_category: "blog",
    });
  },

  // ══════════════════════════════════════════════════════════
  // ENGAGEMENT EVENTS
  // ══════════════════════════════════════════════════════════

  // Calendly modal events
  calendlyModalOpened: (source: string) => {
    trackEvent("calendly_modal_opened", {
      source,
      event_category: "engagement",
      timestamp: Date.now(),
    });
  },

  calendlyModalClosed: (source: string, timeSpentMs?: number) => {
    trackEvent("calendly_modal_closed", {
      source,
      event_category: "engagement",
      time_spent_ms: timeSpentMs,
    });
  },

  // Calendly date/time selected (intermediate step)
  calendlyDateSelected: (source: string) => {
    trackEvent("calendly_date_selected", {
      source,
      event_category: "engagement",
    });
  },

  // CTA click events
  ctaClicked: (ctaName: string, location: string) => {
    trackEvent("cta_clicked", {
      cta_name: ctaName,
      location,
      event_category: "engagement",
    });
  },

  // Contact form submitted
  contactFormSubmitted: (subject?: string) => {
    trackEvent("contact_form_submitted", {
      subject,
      event_category: "engagement",
    });
  },

  // FAQ expanded
  faqExpanded: (question: string) => {
    trackEvent("faq_expanded", {
      question,
      event_category: "engagement",
    });
  },

  // Pricing viewed
  pricingViewed: () => {
    trackEvent("pricing_viewed", {
      event_category: "engagement",
    });
  },

  // ══════════════════════════════════════════════════════════
  // NAVIGATION EVENTS
  // ══════════════════════════════════════════════════════════

  // Page view (for SPA navigation)
  pageView: (path: string, title?: string) => {
    trackEvent("page_view", {
      page_path: path,
      page_title: title,
      event_category: "navigation",
    });
  },

  // Scroll depth
  scrollDepth: (percentage: number, path: string) => {
    trackEvent("scroll_depth", {
      percentage,
      page_path: path,
      event_category: "navigation",
    });
  },

  // ══════════════════════════════════════════════════════════
  // EXIT INTENT POPUP EVENTS
  // ══════════════════════════════════════════════════════════

  // Exit popup shown to user
  exitPopupShown: () => {
    trackEvent("exit_popup_shown", {
      event_category: "engagement",
    });
  },

  // Exit popup dismissed by user
  exitPopupDismissed: () => {
    trackEvent("exit_popup_dismissed", {
      event_category: "engagement",
    });
  },

  // Phone number clicked in exit popup
  exitPopupPhoneClicked: () => {
    trackConversion("exit_popup_phone_clicked", 0, {
      event_category: "conversion",
      phone_number: "05 17 94 84 03",
    });
  },

  // ══════════════════════════════════════════════════════════
  // USER IDENTIFICATION
  // ══════════════════════════════════════════════════════════

  // Set user ID to link sessions
  setUserId,
};
